/**
 * @author Pranav Ramachandran
 * Shuttle component variations (Tree/Normal)
 */

import React, { useEffect, useState, useCallback, useContext } from 'react';
import { ButtonGroup, Button, ListGroup, ListGroupItem, Input } from 'reactstrap';

import 'assets/sass/media-library.scss';

import useTreeModel from 'components/common/tree/tree.model';
import ScTree from 'components/common/tree/tree';
const variations = {
    DEFAULT: 'default',
    TREE: 'tree'
};
import { TreeCacheContext } from 'components/common/tree/tree.cache.context';
import { PRODUCT } from 'components/common/productSearch/productSearch.constant';

export default function ShuttleBox(inputs) {
    const ctx = useContext(TreeCacheContext);
    const { value, options, formInfo } = inputs;
    const [data, setDatas] = useState({
        selectedItems: [],
        options: []
    });
    const [lhsQuery, setlhsQuery] = useState('');
    const [rhsQuery, setrhsQuery] = useState('');
    const checkable = true;
    const disableCheckOnFolder = true;
    let cache = false;
    if (formInfo && formInfo.formID) {
        if (formInfo.formID === PRODUCT) {
            cache = true
        }
    }

    const [
        treeData,
        loadedKeys,
        pwd, setpwd,
        refreshNode,
        getNodeData,
        nodeLookUp,
        expandedKeys,
        setupExpandedKeys,
        selectedKeys,
        setupSelectedKeys,
        checkedKeys,
        setupCheckedKeys,
        loading,
        treeVersion
    ] = useTreeModel({
        initialNode: inputs.folderId ? inputs.folderId : -1,
        customProps: inputs.customProps,
        useModel: inputs.variation === variations.TREE,
        cache: cache,
        context: cache ? ctx : null
    });

    const buildModel = useCallback((selectedKeys, allOptions) => {
        selectedKeys = selectedKeys || [];
        const selectedNodes = [];
        const optionsList = [];

        allOptions.forEach(option => {
            let inLHS = !selectedKeys.includes(option.id);
            let query = inLHS ? lhsQuery : rhsQuery;

            optionsList.push({
                ...option,
                selected: data.selectedItems.includes(option.id),
                visible: String(option.text).toLocaleLowerCase().includes(query.toLocaleLowerCase()),
                isInLhs: inLHS
            });
            if (inputs.variation == variations.TREE && (data.selectedItems.includes(option.id) || selectedKeys.includes(option.id))) {
                selectedNodes.push(option.id);
            }
        });
        if (inputs.variation == variations.TREE) {
            setupCheckedKeys(selectedNodes.map(item => String(item)), false);
        }

        setDatas((prevState) => {
            return {
                ...prevState,
                options: optionsList
            }
        });

    }, [lhsQuery, rhsQuery, data.selectedItems, inputs.variation, setupCheckedKeys]);

    function toggleData(e, isLHS, toggleStatus) {
        e.preventDefault();
        let selectedItemList = [];
        let newOptions = data.options.map((item) => {

            if (!item.visible) {
                return item;
            }

            if ((isLHS && item.isInLhs) || (!isLHS && !item.isInLhs)) {
                if (toggleStatus) {
                    selectedItemList.push(item.id);
                }
                return {
                    ...item,
                    selected: toggleStatus
                }
            }
            if (item.selected) {
                selectedItemList.push(item.id);
            }
            return item;
        });
        setDatas({
            ...data,
            options: newOptions,
            selectedItems: selectedItemList
        });
    }

    function applyData(toRHS) {
        let newOptions = [];
        let options = [...data.options];
        let newSelectedItems = [...data.selectedItems];
        let count = 0;


        if (toRHS) {
            if (inputs.variation === variations.TREE) {
                if (checkedKeys && checkedKeys.length) {
                    checkedKeys.forEach((data) => {
                        let node = nodeLookUp.get(Number(data));
                        if (!node) {
                            return;
                        }
                        let optionData = options.find(item => item.id == node.key);
                        if (!optionData) {
                            if (!newSelectedItems.includes(node.key)) {
                                newSelectedItems.push(node.key);
                            }
                            options.push({
                                ...node,
                                text: node.name,
                                id: node.key,
                                selected: true,
                                visible: true,
                                isInLhs: true
                            });
                        }

                    });
                } else {
                    return;
                }
            }
            newOptions = options.map((item) => {
                if (item.selected && item.visible) {
                    count++;
                    return {
                        ...item,
                        isInLhs: false
                    }
                }
                return item;
            });
            if (count === 0) {
                return;
            }
        } else {
            newOptions = data.options.map((item) => {
                if (item.selected && item.visible) {
                    count++;
                    return {
                        ...item,
                        isInLhs: true,
                        selected: inputs.variation === variations.TREE ? false : true
                    }
                }
                return item;
            });
            if (count === 0) {
                return;
            }

            if (inputs.variation === variations.TREE) {
                let a = newOptions.filter(item => !item.isInLhs).map((item => String(item.id)));
                let keysnew = checkedKeys.filter((key) => !a.includes(key));
                setupCheckedKeys(keysnew, false);
                newSelectedItems = newOptions.filter(item => !item.isInLhs && item.selected);
            }
        }
        setDatas({
            ...data,
            options: newOptions,
            selectedItems: newSelectedItems
        });

        inputs.onChange({
            target: {
                inputtype: inputs.variation === variations.DEFAULT ? 'objects' : 'media', name: inputs.name,
                value: inputs.variation === variations.DEFAULT ? newOptions.filter(item => !item.isInLhs).map((item) => {
                    return item.id;
                }) : newOptions.filter(item => !item.isInLhs).map((item) => {
                    let newObj = { ...item };
                    delete newObj.visible;
                    delete newObj.selected;
                    delete newObj.isInLhs
                    return newObj;
                })
            }
        });
    }

    useEffect(() => {
        if (inputs.variation == variations.DEFAULT) {
            buildModel(value, options);
        } else {
            let keys = value && value.map(item => item.id);
            buildModel(keys, value || []);
        }
    }, [buildModel, inputs.variation, value, options]);

    function selectList(e, id) {
        e.preventDefault();
        let selectedItemList = [];

        let newOptions = data.options.map((item) => {
            let selection = item.id == id ? !item.selected : item.selected;
            if (selection) {
                selectedItemList.push(item.id);
            }
            return {
                ...item,
                selected: selection
            }
        });

        setDatas({
            ...data,
            options: newOptions,
            selectedItems: selectedItemList
        });
    }

    return (
        <div className="align-items-center d-flex shuttle-container col-md-12 p-0">
            <div className="shuttle-box-container col-md-5 p-0">
                {inputs.variation === variations.DEFAULT && <div className="buttons text-right">
                    <Button size="sm" color="secondary" onClick={(e) => toggleData(e, true, true)}>Select All</Button>
                    <Button size="sm" color="secondary" onClick={(e) => toggleData(e, true, false)}>Deselect All</Button>
                </div>}
                <div className="shuttle-list-container">
                    {/* {inputs.variation === variations.TREE && <div className="shuttle-height">
                    </div>} */}
                    {inputs.variation === variations.DEFAULT && <div className="shuttle-search">
                        <Input value={lhsQuery} type="text" name="lhsQuery" placeholder="Search" onChange={(e) => setlhsQuery(e.target.value)} />
                    </div>}
                    <div className="shuttle-list">
                        {inputs.variation === variations.DEFAULT && <ListGroup>
                            {data.options.map((option) => option.visible && option.isInLhs ?
                                (<ListGroupItem onClick={(e) => selectList(e, option.id)} active={option.selected} key={option.id} tag="a" href="#">{option.text}</ListGroupItem>) : '')}
                        </ListGroup>}
                        {inputs.variation === variations.TREE &&

                            <ScTree input={{
                                treeData,
                                loadedKeys,
                                pwd, setpwd,
                                refreshNode,
                                getNodeData,
                                nodeLookUp,
                                expandedKeys,
                                setupExpandedKeys,
                                selectedKeys,
                                setupSelectedKeys,
                                checkedKeys,
                                setupCheckedKeys,
                                checkable,
                                disableCheckOnFolder,
                                checkedKeys,
                                setupCheckedKeys,
                                treeVersion
                            }} />}
                    </div>
                </div>
            </div>
            <div className="shuttle-buttons">
                <ButtonGroup vertical>
                    <Button disabled={inputs.disabled} onClick={() => applyData(true)}><i className="fa fa-arrow-right" aria-hidden="true"></i></Button>
                    <Button disabled={inputs.disabled} onClick={() => applyData(false)}><i className="fa fa-arrow-left" aria-hidden="true"></i></Button>
                </ButtonGroup>
            </div>
            <div className="shuttle-box-container col-md-5 p-0">
                {inputs.variation === variations.DEFAULT && <div className="buttons text-right">
                    <Button size="sm" color="secondary" onClick={(e) => toggleData(e, false, true)}>Select All</Button>
                    <Button size="sm" color="secondary" onClick={(e) => toggleData(e, false, false)}>Deselect All</Button>
                </div>}
                <div className="shuttle-list-container">
                    {/* {inputs.variation === variations.TREE && <div className="shuttle-height">
                    </div>} */}
                    {inputs.variation === variations.DEFAULT && <div className="shuttle-search">
                        <Input value={rhsQuery} type="text" name="rhsQuery" placeholder="Search" onChange={(e) => setrhsQuery(e.target.value)} />
                    </div>}
                    <div className="shuttle-list">
                        <ListGroup>
                            {data.options.map((option) => {
                                return option.visible && !option.isInLhs ? (<ListGroupItem onClick={(e) => selectList(e, option.id)} active={option.selected} key={option.id} tag="a" href="#">{option.text}</ListGroupItem>) : ''
                            })}
                        </ListGroup>
                    </div>
                </div>
            </div>
        </div >
    )
}
